import { HttpBackend, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/firestore';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { b64toBlob, blobToFile } from '@app/shared/functions/b64ToBlob';
import { environment } from '@env/environment';
import firebase from 'firebase/app';
import 'firebase/firestore';
import * as _ from 'lodash';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, first, map, switchMap, take, tap } from 'rxjs/operators';
import * as uuidv4 from 'uuid/v4';
import { ProviderConfigurationVo, Symptom, SymptomConfiguration, SymptomQuestion } from '.';
import {
  AddressBookEntryVo,
  ChronicConditionVo,
  CommunicationTemplateVo,
  NotificationStatusView,
  NotificationVo,
  PatientEventVo,
  ReminderVo,
  RestApiResultOfGuid,
  UpdateAddressBookEntry,
} from './api-client.service';
import { ConfigService } from './config.service';
import { MerakiAuthService } from './meraki-auth.service';
import { DevelopmentalScreening } from './meraki-models/developmental-screening.models';
import { convertPatientToFirestore, fromTimestamp } from './meraki-models/meraki-model-util';
import { EmailRequest, NexResponse, PathologyReportFirestoreVo, SendEmailResponsePayload } from './meraki-models/meraki-models';
import { Practice } from './meraki-models/practice.models';
import { Questionnaire } from './meraki-models/questionnaires.models';
import { VaccinationHistory } from './meraki-models/vaccination-history.models';
import { HttpClientMeraki, MERAKI_API_GATEWAY, MERAKI_CACHE_LB } from './meraki-nexus.providers';
import { DischemOrder, DischemPharmacy, DischemScriptRequest } from './models/dischem.model';
import { GenericMedicine } from './view-models/generic-medicine-view-model.utils';
import { KahunAppointment } from './view-models/kahun-view-model.utils';
import { AdImpression } from './view-models/smartmeds-ads-models';
import Timestamp = firebase.firestore.Timestamp;

export interface VisitReasonViewModel extends Symptom, SymptomConfiguration {}

export interface VisitReasonQuestionViewModel extends SymptomQuestion {
  Active: boolean;
}

export interface CommonChronicConditionsVo extends ChronicConditionVo {
  CommonForSpecialties: Array<string>;
}

export interface GenericMedicineRequest {
  FsCollection: string;
  BaseGroupName: string;
  CommunityName: string;
  CohortName: string;
  BPN_TPN: string;
  ATC5Code: string;
  DrugFamilyGroup: string;
  NAPPICode7: string;
  PatientInfo: any;
  View: string;
}

export interface VaccinatedStatusVo {
  // Pfizer: Array<string> | null;
  // JohnsonAndJohnson: Array<string> | null;
  // Other: Array<string> | null;
  Notes: string | null;
  VaccineStatus: string | null;
}

const PRACTICE_ROOT = 'ClinicalPractice';
const PROVIDER_ROOT = 'ClinicalProvider';
const ENCOUNTER_ROOT = 'Encounter';

const SMARTMEDS_AD_IMPRESSIONS_ROOT = 'SmartMedsAdImpression';
const KAHUN_PRACTICE = 'KahunPractice';

export enum PathReportFollowuActions {
  InTaskList = 'InTaskList',
  SmsSent = 'SmsSent',
  EmailSent = 'EmailSent',
  WhatsappSent = 'WhatsappSent',
}

@Injectable({
  providedIn: 'root',
})
export class MerakiClientService {
  private http: HttpClientMeraki;
  private baseUrl: string;
  private loadBalancerUrl: string;

  constructor(
    private httpHandler: HttpBackend, // Bypass Auth interceptor since it overrides with florence auth token.
    @Inject(HttpClientMeraki) http: HttpClientMeraki,
    @Inject(MERAKI_API_GATEWAY) baseUrl: string,
    @Inject(MERAKI_CACHE_LB) cacheLbUrl: string,
    private authService: MerakiAuthService,
    private firestore: AngularFirestore,
    private configService: ConfigService
  ) {
    this.http = http;
    //this.http = new HttpClient(httpHandler);

    this.baseUrl = baseUrl ? baseUrl : '';
    this.loadBalancerUrl = cacheLbUrl;
  }

  getDischemActiveToken() {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<string>>(`${this.loadBalancerUrl}/v1/clinical/authentication/dischemToken`, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
        })
      ),
      map(response => response.data.payload)
    );
    /* return this.firestore
      .collection('Configuration')
      .doc('Dischem')
      .valueChanges()
      .pipe(map((data: any) => data.token)); */
  }

  sendDischemScript(request: DischemScriptRequest) {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        //this.http.post<NexResponse<string>>(`${this.loadBalancerUrl}/v1/clinical/dischem/sendDischemScript`, request, {
        this.http.post<NexResponse<string>>(`${this.loadBalancerUrl}/v1/clinical/dischem/dischemNewOrder?req=new-order`, request, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
        })
      ),
      map(response => response.data.payload as unknown as DischemOrder)
    );
  }

  newDischemOrder(request: DischemOrder, token: string) {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.post<NexResponse<string>>(`${this.loadBalancerUrl}/v1/clinical/dischem/dischemNewOrder?req=new-order`, request, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
        })
      ),
      map(response => response.data.payload as unknown as DischemOrder)
    );

    /* return this.http.post<NexResponse<string>>(`${this.loadBalancerUrl}/v1/clinical/dischem/dischemNewOrder?req=new-order`, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        rejectUnauthorized: 'false',
      },
    }); */
  }

  openedSandboxDropdown(practiceTenantId: string, patientId: string, treatingDoctorName: string) {
    const sandboxQuickNotesCollection = this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('SandboxActions')
      .doc(patientId);

    return sandboxQuickNotesCollection.set({
      PracticeTenantId: practiceTenantId,
      PatientId: patientId,
      QuickNotes: true,
      TreatingDoctorName: treatingDoctorName,
    });
  }

  updateSpeechToNotes(tenantId: string, encounterId: string, updatedNotes: string, treatingDoctorName: string, encounterDate: Date) {
    // console.log(encounterId);
    // console.log(tenantId);
    const speechToNotesCollection = this.firestore
      .collection('ClinicalPractice')
      .doc(tenantId)
      .collection('Encounter')
      .doc(encounterId)
      .collection('SpeechToNotes')
      .doc(encounterId)
      .update({
        UpdatedSpeechToNotesSummary: updatedNotes,
        EncounterStartTime: encounterDate,
        TreatingDoctorName: treatingDoctorName,
      });

    return from(speechToNotesCollection);
  }

  updateSpeechToNotesNoteVersion(tenantId: string, encounterId: string, noteVersion: number) {
    const speechToNotesCollection = this.firestore
      .collection('ClinicalPractice')
      .doc(tenantId)
      .collection('Encounter')
      .doc(encounterId)
      .collection('SpeechToNotes')
      .doc(encounterId)
      .update({ NoteVersion: noteVersion });

    return from(speechToNotesCollection);
  }

  createSpeechToNotesCollection(tenantId: string, encounterId: string, recordingDuration: number, micDevice: string) {
    const speechToNotesCollection = this.firestore
      .collection('ClinicalPractice')
      .doc(tenantId)
      .collection('Encounter')
      .doc(encounterId)
      .collection('SpeechToNotes')
      .doc(encounterId);

    return from(
      speechToNotesCollection.set({
        RecordingDuration: recordingDuration,
        MicrophoneDeviceUsed: micDevice,
      })
    );
  }

  listenForTranscription(id: string, practiceTenantId: string): Observable<string | null> {
    return this.firestore
      .collection('ClinicalPractice')
      .doc(practiceTenantId)
      .collection('Encounter')
      .doc(id)
      .collection('SpeechToNotes')
      .doc(id)
      .valueChanges()
      .pipe(
        map(data => (data ? (data['SpeechToNotesSummary'] ? data['UpdatedSpeechToNotesSummary'] : '') : null)),
        tap(res => {}) // Map to transcription field directly
      );
  }

  getRecentCompletedEncounterId(practiceId: string, patientId: string): Observable<any> {
    // First fetch only completed encounters for the patient
    const encounterSnapshot = this.firestore
      .collection('ClinicalPractice')
      .doc(practiceId)
      .collection('Encounter', ref =>
        ref.where('Status', '==', 'Completed').where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .get();

    //use the encounter snapshot to get the most recent completed encounter
    return encounterSnapshot.pipe(
      map(snapshot => {
        if (snapshot.empty) return null;

        // Sort encounters by date descending (most recent first)
        const sortedDocs = snapshot.docs.sort(
          (a, b) => b.data().EncounterLineItems.EncounterHeader.DateOfService - a.data().EncounterLineItems.EncounterHeader.DateOfService
        );

        // Return the EncounterId of the most recent encounter
        return sortedDocs[0].data().EncounterId;
      })
    );
  }

  updatePatientLetterMessageCopied(practiceTenantId: string, patientId: string, currentEncounterId: string, treatingDoctorName: string) {
    return this.getRecentCompletedEncounterId(practiceTenantId, patientId).pipe(
      switchMap(previousEncounterId => {
        return this.firestore
          .collection('Sandbox')
          .doc(practiceTenantId)
          .collection('PatientLetters')
          .doc(practiceTenantId)
          .collection('Encounters')
          .doc(previousEncounterId || currentEncounterId)
          .update({
            letterCopied: true,
            letterCopiedBy: treatingDoctorName,
            letterCopiedTimestamp: new Date(),
          });
      })
    );
  }

  updatePatientLetterMessageDownloaded(
    practiceTenantId: string,
    patientId: string,
    currentEncounterId: string,
    treatingDoctorName: string
  ) {
    return this.getRecentCompletedEncounterId(practiceTenantId, patientId).pipe(
      switchMap(previousEncounterId => {
        return this.firestore
          .collection('Sandbox')
          .doc(practiceTenantId)
          .collection('PatientLetters')
          .doc(practiceTenantId)
          .collection('Encounters')
          .doc(previousEncounterId || currentEncounterId)
          .update({
            letterDownloaded: true,
            letterDownloadedBy: treatingDoctorName,
            letterDownloadedTimestamp: new Date(),
          });
      })
    );
  }

  submitSandboxReferralLetterUserFeedback(feedback: string, rating: string, practiceId: string, patientId: string) {
    const patientLetterCollection = this.firestore.collection('Sandbox').doc(practiceId).collection('ReferralLetters').doc(patientId);

    return patientLetterCollection.update({
      userFeedback: {
        Feedback: feedback,
        Rating: rating,
      },
    });
  }

  submitSandboxPathologyUpdateUserFeedback(feedback: string, rating: string, practiceTenantId: string, pathologyReportId: string) {
    const patientPathologyUpdateCollection = this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('PatientPathologyUpdates')
      .doc(pathologyReportId);

    return patientPathologyUpdateCollection.update({
      userFeedback: {
        Feedback: feedback,
        Rating: rating,
      },
    });
  }

  submitSandboxUserFeedback(feedback: string, rating: string, practiceId: string, patientId: string) {
    return this.getRecentCompletedEncounterId(practiceId, patientId).pipe(
      take(1),
      switchMap(encounterId => {
        const patientLetterCollection = this.firestore
          .collection('Sandbox')
          .doc(practiceId)
          .collection('PatientLetters')
          .doc(practiceId)
          .collection('Encounters')
          .doc(encounterId);

        return patientLetterCollection.update({
          userFeedback: {
            Feedback: feedback,
            Rating: rating,
          },
        });
      })
    );
  }

  getSandboxProjectVotes(projectName: string): Observable<any> {
    return this.firestore
      .collection('Sandbox')
      .doc('project_votes')
      .valueChanges()
      .pipe(map(data => data?.[projectName] || { upvotes: 0, downvotes: 0 }));
  }

  updateSandboxProjectVotes(projectName: string, vote: 'up' | 'down') {
    return this.firestore
      .collection('Sandbox')
      .doc('project_votes')
      .update({
        [projectName]: {
          upvotes: vote === 'up' ? firebase.firestore.FieldValue.increment(1) : firebase.firestore.FieldValue.increment(0),
          downvotes: vote === 'down' ? firebase.firestore.FieldValue.increment(1) : firebase.firestore.FieldValue.increment(0),
        },
      });
  }

  createSandboxSummary(
    bpn: string,
    practiceId: string,
    patientId: string,
    practiceTenantId: string,
    treatingProvider: string
  ): Observable<any> {
    // console.log('bpn', bpn);
    // console.log('practiceId', practiceId);
    // console.log('patientId', patientId);
    // console.log('practiceTenantId', practiceTenantId);

    return this.getRecentCompletedEncounterId(practiceTenantId, patientId).pipe(
      take(1),
      switchMap(encounterId => {
        // console.log('encounterId', encounterId);
        if (encounterId == null) {
          return of(null);
        }
        return this.isSandboxSummaryGCSWritePathAvailable(practiceTenantId, patientId, encounterId).pipe(
          take(1),
          switchMap(available => {
            // console.log('available', available);
            if (available == 'doc does not exist') {
              return this.firestore
                .collection('ClinicalPractice')
                .doc(practiceTenantId)
                .collection('PatientClinicalSummary')
                .doc(patientId)
                .collection('SandboxSummary')
                .doc(encounterId)
                .set({
                  BPN: bpn,
                  TreatingProvider: treatingProvider,
                  PracticeId: practiceId,
                  PracticeTenantId: practiceTenantId,
                  PatientUUID: patientId,
                  CompletedEncounterId: encounterId,
                });
            } else if (available == 'doc does exists without GCSWritePath') {
              // Delete and set operations should be done atomically to avoid race conditions
              const docRef = this.firestore
                .collection('ClinicalPractice')
                .doc(practiceTenantId)
                .collection('PatientClinicalSummary')
                .doc(patientId)
                .collection('SandboxSummary')
                .doc(encounterId);

              return docRef.delete().then(() => {
                return docRef.set({
                  BPN: bpn,
                  TreatingProvider: treatingProvider,
                  PracticeId: practiceId,
                  PracticeTenantId: practiceTenantId,
                  PatientUUID: patientId,
                  CompletedEncounterId: encounterId,
                });
              });
            } else {
              return of(null);
            }
          })
        );
      })
    );
  }

  isSandboxSummaryGCSWritePathAvailable(practiceTenantId: string, patientId: string, completedEncounterId: string): Observable<string> {
    return this.firestore
      .collection('ClinicalPractice')
      .doc(practiceTenantId)
      .collection('PatientClinicalSummary')
      .doc(patientId)
      .collection('SandboxSummary')
      .doc(completedEncounterId)
      .get()
      .pipe(
        take(1),
        map(doc => {
          const data = doc.data();
          // console.log('data', data);
          if (doc.exists) {
            if (data?.GCSWritePath) {
              return 'doc exists with GCSWritePath';
            } else {
              return 'doc does exists without GCSWritePath';
            }
          } else {
            return 'doc does not exist';
          }
        })
      );
  }

  getSandboxPathologyUpdates(practiceTenantId: string): Observable<any> {
    return this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('PatientPathologyUpdates')
      .get()
      .pipe(
        map(snapshot => {
          return snapshot.docs.map(doc => doc.data());
        })
      );
  }

  updatePatientPathologyUpdateOpenedTime(practiceTenantId: string, pathReportId: string, timeSpent: number) {
    return this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('PatientPathologyUpdates')
      .doc(pathReportId)
      .update({ TimeSpentOpened: timeSpent });
  }

  updatePatientPathologyUpdateMessageSent(practiceTenantId: string, pathReportId: string, treatingDoctorName: string) {
    return this.firestore.collection('Sandbox').doc(practiceTenantId).collection('PatientPathologyUpdates').doc(pathReportId).update({
      MessageSent: true,
      MessageSentBy: treatingDoctorName,
      MessageSentTimestamp: new Date(),
    });
  }

  updatePathologyMessageToPatientCopied(practiceTenantId: string, pathReportId: string, treatingDoctorName: string) {
    return this.firestore.collection('Sandbox').doc(practiceTenantId).collection('PatientPathologyUpdates').doc(pathReportId).update({
      MessageCopied: true,
      MessageCopiedBy: treatingDoctorName,
      MessageCopiedTimestamp: new Date(),
    });
  }

  getUnsentPathologyUpdates(practiceTenantId: string): Observable<any> {
    return this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('PatientPathologyUpdates', ref => ref.where('MessageSent', '==', false))
      .get()
      .pipe(
        map(snapshot => {
          return snapshot.docs.map(doc => doc.data());
        })
      );
  }

  addTempPathologyReportToCreateMessageOnDemand(pathologyReportId: string, practiceTenantId: string, report: any): Observable<any> {
    if (report.customInstructionUpdate) {
      this.firestore.collection('Sandbox').doc(practiceTenantId).collection('PatientPathologyUpdates').doc(pathologyReportId).delete();
    }
    return from(
      this.firestore
        .collection('ClinicalPractice')
        .doc(practiceTenantId)
        .collection('PathologyReport')
        .doc(`temp-${pathologyReportId}`)
        .set({
          ...report,
        })
    );
  }

  listenForTempPathologyReportToCreateMessageOnDemand(pathologyReportId: string, practiceTenantId: string): Observable<any> {
    return this.firestore
      .collection('Sandbox')
      .doc(practiceTenantId)
      .collection('PatientPathologyUpdates')
      .doc(pathologyReportId)
      .valueChanges();
  }

  listenForConnectConversations(bpn: string): Observable<any> {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('ConnectConversations', ref => ref.orderBy('LastUpdateTimestamp', 'desc'))
      .valueChanges();
  }

  getLatestConversationTurnSummary(bpn: string, conversationId: string): Observable<any> {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('ConnectConversations')
      .doc(conversationId)
      .collection('ConversationTurnSummaries', ref => ref.where('isActive', '==', true))
      .valueChanges();
  }

  updateConversationTurnSummary(bpn: string, conversationId: string, summary: any, seenBy: string) {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('ConnectConversations')
      .doc(conversationId)
      .collection('ConversationTurnSummaries')
      .doc(summary.TurnSummaryId)
      .update({
        isSeen: true,
        seenAt: new Date(),
        seenBy: seenBy,
      });
  }

  updateConversationTurnSummaryAnalytics(bpn: string, conversationId: string, summaryId: string, timeOpened: number) {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('ConnectConversations')
      .doc(conversationId)
      .collection('ConversationTurnSummaries')
      .doc(summaryId)
      .update({ timeOpenedInSeconds: timeOpened });
  }

  updateWhatsappActionCentreTimeSpent(
    practiceTenantId: string,
    timeSpent: number,
    timeOpenedTimestamp: Date,
    doctorName: string,
    numberOfChatsOpened: number,
    filtersApplied: string[]
  ) {
    return this.firestore.collection('Sandbox').doc(practiceTenantId).collection('WhatsappActionCentre').doc(practiceTenantId).set({
      timeOpenedTimestamp: timeOpenedTimestamp,
      timeClosedTimestamp: new Date(),
      lastUpdateTimestamp: new Date(),
      timeSpentInSeconds: timeSpent,
      doctorName: doctorName,
      numberOfChatsOpened: numberOfChatsOpened,
      filtersApplied: filtersApplied,
    });
  }

  updateWhatsappActionCentreAnalytics(practiceTenantId: string, doctorName: string, analytics: any) {
    return this.firestore.collection('Sandbox').doc(practiceTenantId).collection('WhatsappActionCentre').doc(practiceTenantId).set({
      doctorName: doctorName,
      analytics: analytics,
      analyticsUpdatedAt: new Date(),
    });
  }

  submitWhatsappActionCentreFeedback(
    practiceTenantId: string,
    conversationTurnSummaryId: string,
    conversationId: string,
    bpn: string,
    doctorName: string,
    rating: 'positive' | 'negative',
    feedback?: string | null
  ) {
    if (!practiceTenantId || !conversationId || !conversationTurnSummaryId || !doctorName || !bpn) {
      return of(null);
    }

    return from(
      this.firestore
        .collection('Practice')
        .doc(bpn)
        .collection('ConnectConversations')
        .doc(conversationId)
        .collection('ConversationTurnSummaries')
        .doc(conversationTurnSummaryId)
        .set(
          {
            userFeedback: {
              doctorName: doctorName,
              practiceTenantId: practiceTenantId,
              rating: rating,
              feedback: feedback ?? null,
              submittedAt: new Date(),
            },
          },
          { merge: true }
        )
    );
  }

  isPracticeEnabledForSpeechToNotes(bpn: string, hpcsaNumber: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          var isSpeechToNotesEnabled = practice?.Settings?.ClinicalCoPilotSettings?.SpeechToNotes?.Enabled == true;
          var isEnabledDoctorsEmpty = practice?.Settings?.ClinicalCoPilotSettings?.SpeechToNotes?.EnabledDoctors?.length == 0;

          if (isSpeechToNotesEnabled) {
            return isEnabledDoctorsEmpty
              ? true
              : practice?.Settings?.ClinicalCoPilotSettings?.SpeechToNotes?.EnabledDoctors?.includes(hpcsaNumber);
          }
        })
      );
  }

  isPracticeEnabledForSandboxProjects(bpn: string, hpcsaNumber: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          var isSandboxEnabled = practice?.Settings?.SandboxSettings?.Enabled == true;
          var isEnabledDoctorsEmpty = practice?.Settings?.SandboxSettings?.EnabledDoctors?.length == 0;

          if (isSandboxEnabled) {
            return isEnabledDoctorsEmpty ? true : practice?.Settings?.SandboxSettings?.EnabledDoctors?.includes(hpcsaNumber);
          }
          return false;
        })
      );
  }

  isPracticeEnabledForSendWhatsAppMessagesFromSandbox(bpn: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          return practice?.Settings?.SandboxSettings?.EnabledSendWhatsAppMessages || false;
        })
      );
  }

  isPracticeEnabledForWhatsAppActionCentre(bpn: string, hpcsaNumber: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          var isEnabled = practice?.Settings?.PatientConnect?.ActionCentre?.Enabled || false;
          var isEnabledDoctorsEmpty = practice?.Settings?.PatientConnect?.ActionCentre?.EnabledDoctors?.length == 0;
          if (isEnabled) {
            return isEnabledDoctorsEmpty ? true : practice?.Settings?.PatientConnect?.ActionCentre?.EnabledDoctors?.includes(hpcsaNumber);
          }
          return false;
        })
      );
  }

  isPracticeEnabledForExperiments(bpn: string, hpcsaNumber: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          var isEnabled = practice?.Settings?.Experiments?.Enabled || false;
          var isEnabledDoctorsEmpty = practice?.Settings?.Experiments?.EnabledDoctors?.length == 0;
          if (isEnabled) {
            return isEnabledDoctorsEmpty ? true : practice?.Settings?.Experiments?.EnabledDoctors?.includes(hpcsaNumber);
          }
          return false;
        })
      );
  }

  isPracticeAgentEnabled(practiceTenantId: string, practiceId: string): Observable<boolean> {
    if (practiceTenantId == null || practiceId == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Experiments/Settings/Practice/${practiceTenantId}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const settings = data.data() as any;
          const practicePartnerAgent = settings?.FeatureFlags?.['practice-partner-agent'];
          const isEnabled = practicePartnerAgent?.Enabled || false;
          const enabledDoctors = practicePartnerAgent?.EnabledDoctors || [];
          return isEnabled && enabledDoctors.includes(practiceId);
        })
      );
  }

  isPracticeEnabledForRevenueCareEvents(bpn: string, hpcsaNumber: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          var isEnabled = practice?.Settings?.MaximizeLegitRevenueCareEvents?.Enabled || false;
          var isEnabledDoctorsEmpty = practice?.Settings?.MaximizeLegitRevenueCareEvents?.EnabledDoctors?.length == 0;
          if (isEnabled) {
            return isEnabledDoctorsEmpty ? true : practice?.Settings?.MaximizeLegitRevenueCareEvents?.EnabledDoctors?.includes(hpcsaNumber);
          }
          return false;
        })
      );
  }

  isPracticeEnabledForPatientAI(bpn: string): Observable<boolean> {
    if (bpn == null) {
      return of(false);
    }
    return this.firestore
      .doc(`Practice/${bpn}`)
      .get()
      .pipe(
        map(data => {
          if (!data.exists) {
            return false;
          }
          const practice = data.data() as Practice;
          // support both typo version and expected version
          return (
            practice?.Settings?.ClinicalCoPilotSettings?.PatientHistorySummary?.Enabled == true ||
            practice?.Settings?.ClincialCoPilotSettings?.PatientHistorySummary?.Enabled == true
          );
        })
      );
  }

  getCareEventNotifications(practiceTenantId: string, patientId: string): Observable<any> {
    return this.firestore
      .collection(`ClinicalPractice/${practiceTenantId}/MaximizeLegitRevenueCareEvents/${patientId}/CareEventNotifications`)
      .doc('latest')
      .get()
      .pipe(
        map(snapshot => {
          return snapshot.data();
        }),
        catchError(error => {
          console.error(error);
          return of(null);
        })
      );
  }

  getPatientClinicalSummary(practiceTenantId: string, patientId: string): Observable<any> {
    return this.firestore
      .collection(
        `ClinicalPractice/${practiceTenantId}/PatientClinicalSummary/${patientId}/CheckInSummary`,
        ref =>
          ref
            .orderBy('CreatedAt', 'desc') // Apply ordering within the query function
            .limit(1) // Limit to the most recent document
      )
      .valueChanges<string>({ idField: 'id' })
      .pipe(
        /* map(querySnapshot => {
          // Extract the first document from the query snapshot
          const doc = querySnapshot.docs[0];
          const summaryDoc: any = doc.data();
          summaryDoc.id = doc.id;
          return doc ? summaryDoc : null;
        }), */
        first(),
        map(doc => doc.length > 0 && doc[0]),
        map(s => s && { ...s, CreatedAt: fromTimestamp(s.CreatedAt) })
      );
  }

  updatePatientClinicalSummaryHTML(
    practiceTenantId: string,
    patientId: string,
    checkInSummaryId: string,
    summaryHTML: string
  ): Observable<any> {
    return from(
      this.firestore
        .collection(`ClinicalPractice/${practiceTenantId}/PatientClinicalSummary/${patientId}/CheckInSummary`)
        .doc(checkInSummaryId)
        .set({ SummaryEditedFinalHTML: summaryHTML }, { merge: true })
    );
  }

  updatePatientClinicalSummaryDictatedNotes(
    practiceTenantId: string,
    patientId: string,
    checkInSummaryId: string,
    dictatedNotes: string
  ): Observable<any> {
    return from(
      this.firestore
        .collection(`ClinicalPractice/${practiceTenantId}/PatientClinicalSummary/${patientId}/CheckInSummary`)
        .doc(checkInSummaryId)
        .set({ DictatedNotes: dictatedNotes }, { merge: true })
    );
  }

  logClinicalSummaryOpenEvent(practiceTenantId: string, patientId: string, docId: string): Observable<any> {
    // Reference to the document to update
    const documentRef = this.firestore
      .collection(`ClinicalPractice/${practiceTenantId}/PatientClinicalSummary/${patientId}/CheckInSummary`)
      .doc(docId);

    // Reference to the events collection
    const eventsCollectionRef = this.firestore.collection(
      `ClinicalPractice/${practiceTenantId}/PatientClinicalSummary/${patientId}/CheckInSummary/${docId}/Events`
    );

    return from(eventsCollectionRef.add({ openTime: new Date() })).pipe(
      switchMap(docRef => from(documentRef.update({ OpenWindow: false })).pipe(map(() => ({ success: true, eventId: docRef.id })))),
      catchError(error => {
        throw error;
      })
    );
  }

  logClinicalSummaryCloseEvent(
    practiceId: string,
    patientId: string,
    feedback: string,
    thumbState: string,
    docId: string,
    hideSummaryPreference: boolean,
    eventId: string,
    analytics?: any
  ): Observable<any> {
    // Reference to the document to update
    const documentRef = this.firestore
      .collection(`ClinicalPractice/${practiceId}/PatientClinicalSummary/${patientId}/CheckInSummary`)
      .doc(docId);

    // Reference to the specific event document
    const eventDocumentRef = this.firestore
      .collection(`ClinicalPractice/${practiceId}/PatientClinicalSummary/${patientId}/CheckInSummary/${docId}/Events`)
      .doc(eventId);

    const eventUpdateData: any = {
      feedback,
      thumbState,
      closeTime: new Date(),
      hideSummaryPreference,
      hasEdited: analytics?.hasEdited ?? false,
      savedToTimeline: analytics?.savedToTimeline ?? false,
      dictatedNotes: analytics?.dictatedNotes ?? false,
    };

    if (analytics?.editTimestamp) {
      eventUpdateData.editTimestamp = analytics.editTimestamp;
    }

    if (analytics?.timelineSaveTimestamp) {
      eventUpdateData.timelineSaveTimestamp = analytics.timelineSaveTimestamp;
    }

    if (analytics?.messageScroll) {
      eventUpdateData.messageScrollAnalytics = analytics.messageScroll;
    }

    if (analytics?.timelineScroll) {
      eventUpdateData.timelineScrollAnalytics = analytics.timelineScroll;
    }

    return from(documentRef.update({ OpenWindow: false })).pipe(
      switchMap(() => from(eventDocumentRef.update(eventUpdateData))),
      map(() => {
        return { success: true };
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  sendEmail(request: EmailRequest): Observable<NexResponse<SendEmailResponsePayload>> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .post<NexResponse<SendEmailResponsePayload>>(`${this.baseUrl}/v1/communication/email/sendEmail`, request, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      )
    );
  }

  requestPatientSummary(provider: ProviderConfigurationVo, patientEvent: PatientEventVo) {
    var request = {
      BPN: provider.PracticeNumber,
      TPN: provider.TreatingPracticeNumber,
      PracticeTenantId: provider.PracticeTenantId,
      PracticeId: provider.PracticeId,
      PatientId: patientEvent.Patient.PatientXRef,
      PatientUUID: patientEvent.Patient.PatientId,
      EventId: patientEvent.PatientEventId,
      UpdatedDate: firebase.firestore.FieldValue.serverTimestamp(),
    };
    return (
      this.firestore
        // .collection('Practice')
        // .doc(provider.PracticeNumber)
        .collection('ClinicalPractice')
        .doc(provider.PracticeTenantId)
        .collection('CalendarEventCheckin')
        .doc(patientEvent.PatientEventId)
        .set(request, { merge: true })
    );

    /* return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http // todo base url via gateway
          //.post<any>(`${this.baseUrl}/clinicialai-create-patient-timeline-summary-http`,
          .post<any>(`https://europe-west1-healthbridge-meraki-dev.cloudfunctions.net/clinicialai-create-patient-timeline-summary-http`,
            request,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Skip-Progress-Bar': 'true', // skip progress bar as call is long and we don't want to show annoying loader
              },
            })
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            }),
          ),
      ),
    ); */
  }

  loadBenefitCheckReport(bpn: string, referenceNum: string): Observable<string> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .get<NexResponse<any>>(`${this.baseUrl}/v1/calendar/bcpdf/${bpn}/${referenceNum}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            }),
            map(response => response.text),
            map(response => response?.replace('data:application/pdf;base64,', '')),
            map(response => URL.createObjectURL(blobToFile(b64toBlob(response, 'application/pdf;base64'), referenceNum + '.pdf')))
          )
      )
    );
  }

  getBasicCommunicationTemplates() {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc('00000000-0000-0000-0000-000000000000')
      .collection('CommunicationTemplate')
      .valueChanges()
      .pipe(
        map(data => data.map(d => ({ ...d, LastUpdated: d.LastUpdated.toDate() }) as CommunicationTemplateVo)),
        first()
      );
  }

  private communicationTemplateCollectionRef(practiceTenantId: string) {
    return this.firestore.collection(PRACTICE_ROOT).doc(practiceTenantId).collection('CommunicationTemplate');
  }

  removeCommunicationTemplate(practiceTenantId: string, templateId: string) {
    // todo check if we want to permanently delete, no cqrs history to revert that
    return from(this.communicationTemplateCollectionRef(practiceTenantId).doc(templateId).delete());
  }

  updateCommunicationTemplate(template: CommunicationTemplateVo) {
    return from(
      this.communicationTemplateCollectionRef(template.PracticeId)
        .doc(template.TemplateId)
        .set({ ...template, LastUpdated: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true })
    ).pipe(
      map(
        s => ({ Sucess: true }) as RestApiResultOfGuid,
        catchError(error => {
          return of({ Sucess: false, ResponseMessage: 'Firestore write error' } as RestApiResultOfGuid);
        })
      )
    );
  }

  getCommunicationTemplates(practiceTenantId: string, templateTypes: string[]) {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('CommunicationTemplate', ref => ref.where('TemplateType', 'in', templateTypes))
      .valueChanges()
      .pipe(
        map(data => data.map(d => ({ ...d, LastUpdated: d.LastUpdated?.toDate() || null }) as CommunicationTemplateVo)),
        first()
      );
  }

  getAddressBookAll(practiceId: string): Observable<AddressBookEntryVo[]> {
    const ref: AngularFirestoreCollection<AddressBookEntryVo> = this.addressBookCollectionRef(practiceId);
    return ref.valueChanges().pipe(first());
  }

  getAddressBookEntryById(practiceId: string, addressBookEntryId: string): Observable<AddressBookEntryVo> {
    return this.addressBookCollectionRef(practiceId).doc(addressBookEntryId).valueChanges().pipe(first());
  }

  updateAddressBook(
    practiceId: string,
    addressBookEntryId: string,
    addressBookEntry: UpdateAddressBookEntry
  ): Observable<RestApiResultOfGuid> {
    return from(
      this.addressBookCollectionRef(practiceId)
        .doc(addressBookEntryId)
        .set({
          ...addressBookEntry.AddressBookEntry,
        })
    ).pipe(
      map(s => ({ Sucess: true }) as RestApiResultOfGuid), // todo
      catchError(error => {
        return of({ Sucess: false, ResponseMessage: 'Firestore write error' } as RestApiResultOfGuid);
      })
    );
  }

  removeAddressBook(practiceId: string, addressBookEntryId: string): Observable<RestApiResultOfGuid> {
    return from(this.addressBookCollectionRef(practiceId).doc(addressBookEntryId).delete()).pipe(
      map(s => ({ Sucess: true }) as RestApiResultOfGuid), // todo
      catchError(error => {
        return of({ Sucess: false, ResponseMessage: 'Firestore write error' } as RestApiResultOfGuid);
      })
    );
  }

  getReminders(providerId: string): Observable<ReminderVo[]> {
    const ref: AngularFirestoreCollection<ReminderVo> = this.taskCollectionRef(providerId);
    return ref.valueChanges().pipe(
      map(s =>
        s.map((data: any) => {
          // todo need common approach to convert/use timestamp, for now to not change existing c# model use conversion on fly
          return {
            ...data,
            DueDate: data.DueDate?.toDate(),
            UpdatedDate: data.UpdatedDate?.toDate(),
            CreatedDate: data.CreatedDate?.toDate(),
          };
        })
      ),
      distinctUntilChanged(_.isEqual) // atm after update this fires twice, might be angularfire bug
    );
  }

  updateReminder(providerId: string, reminderId: string, reminder: ReminderVo): Observable<RestApiResultOfGuid> {
    return from(
      this.taskCollectionRef(providerId)
        .doc(reminderId)
        .set({
          ...reminder,
          DueDate: reminder.DueDate && Timestamp.fromDate(reminder.DueDate),
          UpdatedDate: firebase.firestore.FieldValue.serverTimestamp(),
          CreatedDate: reminder.CreatedDate && Timestamp.fromDate(reminder.CreatedDate),
          Description: reminder.Description || '',
        })
    ).pipe(
      map(s => ({ Sucess: true }) as RestApiResultOfGuid),
      catchError(() => of({ Sucess: false }))
    );
  }

  deleteReminder(providerId: string, reminderId: string): Observable<RestApiResultOfGuid> {
    return from(this.taskCollectionRef(providerId).doc(reminderId).delete()).pipe(map(s => ({ Sucess: true }) as RestApiResultOfGuid));
  }

  getSmartGenericMedicine(request: GenericMedicineRequest): Observable<GenericMedicine> {
    return this.authService.getFirebaseToken().pipe(
      take(1),
      switchMap(token =>
        this.http.post<GenericMedicine>(
          `${this.baseUrl}${this.configService.config.smartGenericsSettings.Url}`,
          { message: request },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
      ),
      map(
        medicine =>
          medicine && {
            ...medicine,
            RxData: medicine.RxData.map(m => ({
              ...m,
              GenericGroupMembers: m.GenericGroupMembers.map(gm => ({ ...gm, GroupUUId: uuidv4() })),
            })),
            DxData: medicine.DxData.map(m => ({
              ...m,
              GenericGroupMembers: m.GenericGroupMembers.map(gm => ({ ...gm, GroupUUId: uuidv4() })),
            })),
          }
      )
    );
  }

  getTopNotifications(providerId: string): Observable<NotificationStatusView[]> {
    const ref = this.notificationCollectionRef(providerId);
    return ref.valueChanges().pipe(
      map(s =>
        s.map((data: any) => {
          return {
            ...data,
            Date: data.Date?.toDate(),
          } as NotificationStatusView;
        })
      ),
      map(results => results.splice(0, 5)),
      take(1)
    );
  }

  viewNotification(providerId: string, notificationId: string): Observable<RestApiResultOfGuid> {
    const ref = this.notificationCollectionRef(providerId).doc(notificationId).update({
      Status: 'viewed',
    });

    return from(ref).pipe(
      map(() => ({ Sucess: true }) as RestApiResultOfGuid),
      catchError(error => {
        return of({ Sucess: false, ResponseMessage: 'Firestore write error' } as RestApiResultOfGuid);
      })
    );
  }

  createBulkNotification(notifications: NotificationVo[]) {
    const batch = this.firestore.firestore.batch();

    notifications.forEach(notification => {
      const ref = this.notificationCollectionRef(notification.ProviderId).doc(notification.NotificationId).ref;

      const document = {
        ...notification,
        Date: notification.Date && Timestamp.fromDate(notification.Date),
      };

      batch.set(ref, document);
    });

    return batch.commit();
  }

  getProviders(): Observable<ProviderConfigurationVo[]> {
    const ref: AngularFirestoreCollection<ProviderConfigurationVo> = this.providerCollectionRef();

    return ref.valueChanges().pipe(
      take(1),
      map(s =>
        s.map((data: any) => ({
          ...data,
          BillingEndDate: data.BillingEndDate?.toDate(),
          BillingStartDate: data.BillingStartDate?.toDate(),
          LastUpdatedDate: data.LastUpdatedDate?.toDate(),
        }))
      ),
      distinctUntilChanged(_.isEqual) // atm after update this fires twice, might be angularfire bug
    );
  }

  private providerCollectionRef() {
    return this.firestore.collection(PROVIDER_ROOT);
  }

  private notificationCollectionRef(providerId: string) {
    return this.firestore
      .collection(PROVIDER_ROOT)
      .doc(providerId)
      .collection('Notification', ref => ref.orderBy('Date', 'desc'));
  }

  private taskCollectionRef(providerId: string) {
    const reminderRef = this.firestore.collection(PROVIDER_ROOT).doc(providerId).collection('Task');
    return reminderRef;
  }

  private addressBookCollectionRef(practiceTenantId: string) {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection<AddressBookEntryVo>('AddressBook', ref => ref.orderBy('Name'));
  }

  getUndeliveredPathologyReports(practiceTenantId: string, dateFrom: Date, dateTo: Date): Observable<PathologyReportFirestoreVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport', q => {
        const query = q.where('DeliveryStatus', '==', 'Undelivered');

        if (dateFrom && dateTo) {
          return query
            .where('DeliveryDate', '>=', Timestamp.fromDate(dateFrom))
            .where('DeliveryDate', '<=', Timestamp.fromDate(dateTo))
            .orderBy('DeliveryDate', 'desc');
        } else if (dateFrom && !dateTo) {
          return query.where('DeliveryDate', '>=', Timestamp.fromDate(dateFrom)).orderBy('DeliveryDate', 'desc');
        } else if (!dateFrom && dateTo) {
          return query.where('DeliveryDate', '<=', Timestamp.fromDate(dateTo)).orderBy('DeliveryDate', 'desc');
        }
        return query;
      })
      .valueChanges()
      .pipe(
        map((data: any[]) =>
          data.map(item => ({
            ...this.toPathReportObject(item),
            DeliveryDate: item.DeliveryDate?.toDate() || null,
          }))
        )
        // map(data => data.filter(d => d.DeliveryStatus === 'Undelivered') || data)
      );
  }

  getHtmlBodyFromStorage(billingPracticeNo: string, path: string, communicationId: string) {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .get<{ success: boolean; contents: string }>(
            `${this.baseUrl}/v2/files/practices/${billingPracticeNo}?file=${path}/${communicationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      ),
      map(s => s.contents)
    );
  }

  updateHtmlToStorage(
    billingPracticeNo: string,
    path: string,
    id: string,
    content: string
  ): Observable<{ Success: boolean; Data: string }> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .post<{ success: boolean; error: { status: string; message: string; code: string; correlationId: string } }>(
            `${this.baseUrl}/v1/files/practices/${billingPracticeNo}?file=${path}/${id}`,
            {
              content,
              contentType: 'text/html',
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      ),
      map(s => ({ Data: content, Success: s.success }))
    );
  }

  getPathologyReportXml(billingPracticeNo: string, pathologyReportId: string) {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .get<{ success: boolean; contents: string }>(
            `${this.baseUrl}/v1/files/practices/${billingPracticeNo}/PathologyReports/${pathologyReportId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      ),
      map(s => s.contents)
    );
  }

  getPathologyReportPdf(billingPracticeNo: string, pathologyReportId: string) {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .get<{ success: boolean; contents: string }>(
            `${this.baseUrl}/v2/files/practices/${billingPracticeNo}?file=PathologyReports/${pathologyReportId}.pdf`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      ),
      map(s => s.contents)
    );
  }

  getPathologyReportWithXml(practiceTenantId: string, pathologyReportId: string): Observable<PathologyReportFirestoreVo> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport')
      .doc(pathologyReportId)
      .valueChanges()
      .pipe(
        map(s => this.toPathReportObject(s)),
        switchMap(pathologyReport =>
          pathologyReport.Source === 'cloud'
            ? this.getPathReportStructuredXml(practiceTenantId, pathologyReport).pipe(
                map(path => ({ ...pathologyReport, PathologyReport: path }))
              )
            : of(pathologyReport)
        ),
        distinctUntilChanged(_.isEqual)
      );
  }

  getPathologyReport(practiceTenantId: string, pathologyReportId: string): Observable<PathologyReportFirestoreVo> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport')
      .doc(pathologyReportId)
      .valueChanges()
      .pipe(
        map(s => this.toPathReportObject(s)),
        distinctUntilChanged(_.isEqual)
      );
  }

  getPathReportStructuredXml(practiceTenantId: string, pathologyReport: PathologyReportFirestoreVo) {
    if (pathologyReport.XmlReference != null) {
      return this.firestore
        .doc(pathologyReport.XmlReference)
        .collection('xml')
        .doc(pathologyReport.PathologyReportId)
        .valueChanges(first());
    }
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport')
      .doc(pathologyReport.PathologyReportId)
      .collection('xml')
      .doc(pathologyReport.PathologyReportId)
      .valueChanges(first());
  }

  getAllPathologyReportsForPatient(practiceTenantId: string, patientId: string): Observable<PathologyReportFirestoreVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport', r => r.where('PatientIds', 'array-contains', patientId))
      .snapshotChanges()
      .pipe(
        // Skip ephemeral `temp-*` trigger docs written by addTempPathologyReportToCreateMessageOnDemand.
        map(actions => actions.filter(a => !a.payload.doc.id.startsWith('temp-'))),
        map(actions => actions.map(a => a.payload.doc.data() as any)),
        map((data: any[]) =>
          data.map(item => ({
            ...this.toPathReportObject(item),
            DeliveryDate: item.DeliveryDate?.toDate() || null,
          }))
        )
      );
  }

  getPathologyReportsByPatientIds(practiceTenantId: string, patientIds: string[]): Observable<PathologyReportFirestoreVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport', r =>
        r.where('DeliveryStatus', '==', 'Delivered').where('PatientIds', 'array-contains-any', patientIds)
      )
      .snapshotChanges()
      .pipe(
        // Skip ephemeral `temp-*` trigger docs written by addTempPathologyReportToCreateMessageOnDemand.
        map(actions => actions.filter(a => !a.payload.doc.id.startsWith('temp-'))),
        map(actions => actions.map(a => a.payload.doc.data() as any)),
        map((data: any[]) =>
          data.map(item => ({
            ...this.toPathReportObject(item),
            DeliveryDate: item.DeliveryDate?.toDate() || null,
          }))
        )
      );
  }

  getPathologyReports(
    practiceTenantId: string,
    primaryProviderId: string,
    dateFrom: Date,
    dateTo: Date,
    reportStates: string[],
    followupActions: string[]
  ): Observable<PathologyReportFirestoreVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport', q => {
        const query = q.where('DeliveryStatus', '==', 'Delivered');
        if (dateFrom && dateTo) {
          return query
            .where('ReceivedDate', '>=', Timestamp.fromDate(dateFrom))
            .where('ReceivedDate', '<=', Timestamp.fromDate(dateTo))
            .orderBy('ReceivedDate', 'desc');
        } else if (dateFrom && !dateTo) {
          return query.where('ReceivedDate', '>=', Timestamp.fromDate(dateFrom)).orderBy('ReceivedDate', 'desc');
        } else if (!dateFrom && dateTo) {
          return query.where('ReceivedDate', '<=', Timestamp.fromDate(dateTo)).orderBy('ReceivedDate', 'desc');
        }
        /*
        // todo need complex index to make it work, for now filter in memory
        if (!!primaryProviderId) {
          return query != null
            ? query.where('PrimaryProviderId', '==', primaryProviderId)
            : q.where('PrimaryProviderId', '==', primaryProviderId);
        } */
        return query;
      })
      .snapshotChanges()
      .pipe(
        // `addTempPathologyReportToCreateMessageOnDemand` writes a sibling doc at
        // `temp-{PathologyReportId}` as a cloud-function trigger; its data spreads the
        // original report so it satisfies the same filter and shows up as a phantom
        // duplicate row. Skip these here so no caller has to dedupe.
        map(actions => actions.filter(a => !a.payload.doc.id.startsWith('temp-'))),
        map(actions => actions.map(a => a.payload.doc.data() as any)),
        map((data: any[]) => data.map(item => this.toPathReportObject(item))),
        map(data => (reportStates.length > 0 && data.filter(d => reportStates.includes(d.StatusType))) || data),
        map(
          data =>
            (followupActions.length > 0 && data.filter(d => d.FollowupActions?.every(s => !followupActions?.includes(s.Type)) || false)) ||
            data
        )
      );
  }

  private toPathReportObject(item: any): PathologyReportFirestoreVo {
    return {
      ...item,
      ReceivedDate: item.ReceivedDate?.toDate() || null,
      DeliveryDate: item.DeliveryDate?.toDate() || null,
      // handle use case when during merging we didn't find workflow for path report and status is not set
      StatusType: item.StatusType && item.StatusType !== 'Urgent' ? item.StatusType : 'Read',
      FollowupActions: item.FollowupActions?.map(s => ({ ...s, Date: s.Date && s.Date.toDate() })) || [],
      PatientInfoFromReport: item?.PatientInfoFromReport && convertPatientToFirestore(item?.PatientInfoFromReport),
      PatientContactNo: item?.PatientInfoFromReport?.PatientAccountDetails.MedicalAidMainMemberDetails.ContactNo || null,
      PathologyReport: null, // ignore xml info to lightweight object, ideally ignore even before reading from firestore
      ContentTypes: item?.ContentTypes || [],
    };
  }

  linkPathReportToPatient(practiceTenantId: string, primaryProviderId: string, pathologyReportId: string, patientIds: string[]) {
    return from(
      this.firestore.collection(PRACTICE_ROOT).doc(practiceTenantId).collection('PathologyReport').doc(pathologyReportId).update({
        PatientIds: patientIds,
        StatusType: 'Unread',
        DeliveryStatus: 'Delivered',
        DeliveryDate: Timestamp.now(),
        PrimaryProviderId: primaryProviderId,
        PatientMatchedRule: 'User',
      })
    ).pipe(map(s => patientIds));
  }

  addFollowuActionForPathReport(practiceTenantId: any, pathologyReport: any, action: { Type: string; Date: Date }) {
    return from(
      this.firestore
        .collection(PRACTICE_ROOT)
        .doc(practiceTenantId)
        .collection('PathologyReport')
        .doc(pathologyReport.PathologyReportId)
        .update({ FollowupActions: [...(pathologyReport.FollowupActions || []), action] })
    );
  }

  updatePathologyReportStatus(practiceTenantId: string, pathologyReportId: string, status: string) {
    return from(
      this.firestore
        .collection(PRACTICE_ROOT)
        .doc(practiceTenantId)
        .collection('PathologyReport')
        .doc(pathologyReportId)
        .update({ StatusType: status })
    );
  }

  updatePathologyReportStatuses(group: PathologyReportFirestoreVo[], status: string) {
    const batch = this.firestore.firestore.batch();
    group.forEach(s => {
      batch.update(
        this.firestore.firestore.collection(PRACTICE_ROOT).doc(s.PracticeId).collection('PathologyReport').doc(s.PathologyReportId),
        { StatusType: status }
      );
    });
    return from(batch.commit());
  }

  updatePathologyDeliveryStatus(practiceTenantId: string, pathologyReportId: string, status: string) {
    return from(
      this.firestore
        .collection(PRACTICE_ROOT)
        .doc(practiceTenantId)
        .collection('PathologyReport')
        .doc(pathologyReportId)
        .update({ DeliveryStatus: status })
    );
  }

  /**
   * Unblocks pathology reports with ProviderCreditStatus Locked after a pathology limit increase.
   * Fetches locked docs in batches (no composite index) until up to pathologyResultsUsed have been
   * activated or no locked docs remain.
   */
  unlockLockedPathologyReportsForPractice$(practiceTenantId: string, pathologyResultsUsed: number): Observable<number> {
    const maxU = Math.max(0, Math.floor(pathologyResultsUsed));
    if (!practiceTenantId || maxU <= 0) {
      return of(0);
    }
    const maxFetchRounds = Math.max(3, Math.ceil(maxU / 500) + 1);

    return this.unlockLockedPathologyReportsBatch$(practiceTenantId, maxU, 0, maxFetchRounds, 0);
  }

  private unlockLockedPathologyReportsBatch$(
    practiceTenantId: string,
    remaining: number,
    totalUnlocked: number,
    maxFetchRounds: number,
    roundIndex: number
  ): Observable<number> {
    if (remaining <= 0 || roundIndex >= maxFetchRounds) {
      return of(totalUnlocked);
    }

    const fetchLimit = Math.min(500, Math.max(remaining, 1));

    return this.firestore
      .collection<PathologyReportFirestoreVo>(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('PathologyReport', ref => ref.where('ProviderCreditStatus', '==', 'Locked').limit(fetchLimit))
      .get()
      .pipe(
        switchMap(snapshot => {
          if (snapshot.empty) {
            return of(totalUnlocked);
          }
          const sortedNewestFirst = [...snapshot.docs].sort(
            (a, b) => (fromTimestamp(b.data()?.ReceivedDate)?.getTime() ?? 0) - (fromTimestamp(a.data()?.ReceivedDate)?.getTime() ?? 0)
          );
          const toUnlock = sortedNewestFirst.slice(0, Math.min(remaining, sortedNewestFirst.length));
          const batch = this.firestore.firestore.batch();
          toUnlock.forEach(d => batch.update(d.ref, { ProviderCreditStatus: 'Active' }));
          return from(batch.commit()).pipe(
            switchMap(() => {
              const unlockedThisRound = toUnlock.length;
              const newTotal = totalUnlocked + unlockedThisRound;
              const stillRemaining = remaining - unlockedThisRound;
              if (stillRemaining <= 0) {
                return of(newTotal);
              }
              if (snapshot.size < fetchLimit) {
                return of(newTotal);
              }
              return this.unlockLockedPathologyReportsBatch$(practiceTenantId, stillRemaining, newTotal, maxFetchRounds, roundIndex + 1);
            })
          );
        })
      );
  }

  getKahunPatientSummary(practice: string, appointmentXRef: string): Observable<KahunAppointment> {
    if (appointmentXRef) {
      return this.firestore
        .collection(KAHUN_PRACTICE)
        .doc(practice)
        .collection('Appointment')
        .doc(appointmentXRef)
        .valueChanges()
        .pipe(
          map(data => data as KahunAppointment),
          distinctUntilChanged(_.isEqual)
        );
    }
    return of(null);
  }

  isKahunPractice(provider: any) {
    return this.firestore
      .collection('Configuration')
      .doc('KahunPractice')
      .valueChanges()
      .pipe(
        first(),
        map((data: any) => {
          const BPN = provider.PracticeNumber;
          const TPN = provider.TreatingPracticeNumber;
          let isKahunPractice = false;
          if (data?.billingNumbers.hasOwnProperty(BPN)) {
            isKahunPractice = data?.billingNumbers[BPN].length ? data?.billingNumbers[BPN].includes(TPN) : true;
          }
          return isKahunPractice;
        }),
        catchError(e => of(false))
      );
  }

  public saveAdImpressions(impressions: AdImpression[]) {
    try {
      const fsImpressions = impressions.filter(i => !!i.EncounterID);

      const batch = this.firestore.firestore.batch();

      fsImpressions.forEach(impression => {
        batch.set(this.firestore.firestore.collection(`${SMARTMEDS_AD_IMPRESSIONS_ROOT}`).doc(), impression);
      });

      batch.commit();
    } catch (err) {
      console.error(`An error occurred while saving ad impressions. ${err}`);
    }
  }

  public async loadAd(sponsorInfo: any) {
    try {
      const baseUrl = environment.smartGenericsSettings.AdsDisplayUrl;
      const result = await this.http
        .post(
          baseUrl,
          {
            message: {
              SponsorInfo: sponsorInfo,
            },
          },
          {
            withCredentials: false,
            observe: 'body',
          }
        )
        .toPromise();
      return result;
    } catch (err) {
      console.error(err);
    }
    return null;
  }

  getQuestionnaires() {
    return this.firestore
      .collection('/Configuration/ClinicalQuestionnaires/Questionnaires')
      .get()
      .pipe(
        take(1),
        map(snapshot =>
          snapshot.docs.map(doc => {
            const questionnaire = doc.data() as Questionnaire;
            return { ...questionnaire, id: doc.id };
          })
        )
      );
  }

  savePatientVaccinationHistory$(practiceTenantId: string, patientId: string, vaccinationData: Partial<VaccinationHistory>) {
    return from(
      this.firestore.collection(`${PRACTICE_ROOT}/${practiceTenantId}/Vaccination`).doc(patientId).set(vaccinationData, { merge: true })
    );
  }

  getPatientVaccinationHistory$(practiceTenantId: string, patientId: string) {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('Vaccination')
      .doc(patientId)
      .valueChanges()
      .pipe(
        map(doc => doc),
        catchError(err => {
          if (err?.code?.includes('permission-denied')) {
            return of(null);
          }
          return throwError(err);
        })
      ) as Observable<VaccinationHistory>;
  }

  updateVaccinationStatus(practiceTenantId: string, patientId: string, AgeGroupUpToDate: string, add: boolean) {
    return from(
      this.firestore
        .collection(`${PRACTICE_ROOT}/${practiceTenantId}/Vaccination`)
        .doc(patientId)
        .set(
          {
            upToDate: add
              ? firebase.firestore.FieldValue.arrayUnion(AgeGroupUpToDate)
              : firebase.firestore.FieldValue.arrayRemove(AgeGroupUpToDate),
          },
          { merge: true }
        )
    );
  }

  getPatientDevelopmentalScreening$(practiceTenantId: string, patientId: string) {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection('DevelopmentalScreening')
      .doc(patientId)
      .valueChanges()
      .pipe(
        map(doc => doc),
        catchError(err => {
          if (err?.code?.includes('permission-denied')) {
            return of(null);
          }
          return throwError(err);
        })
      ) as Observable<DevelopmentalScreening>;
  }

  savePatientDevelopmentalScreening$(practiceTenantId: string, patientId: string, developmentalData: Partial<DevelopmentalScreening>) {
    return from(
      this.firestore
        .collection(`${PRACTICE_ROOT}/${practiceTenantId}/DevelopmentalScreening`)
        .doc(patientId)
        .set(developmentalData, { merge: true })
    );
  }
}
